import { openCropper } from '../file-input-cropper';
import { initLucideIcons } from '../lucide-icons';
import {
    FILE_INPUT_BROWSE_BUTTON_CLASS,
    FILE_INPUT_HIDDEN_ACTION_CLASS,
    FILE_INPUT_PREVIEW_ACTION_BUTTON_CLASS,
    FILE_INPUT_PREVIEW_ERROR_ACTION_BUTTON_CLASS,
    getFileInputIconOptions,
    getFileInputLayoutTemplates,
} from '../file-input-layout';

const AVATAR_UPLOAD_SELECTOR = 'input[data-nais-avatar-upload]';
const AVATAR_INITIALIZED_DATA = 'naisAvatarUploadInitialized';
const AVATAR_NEW_FILE_DATA = 'naisAvatarUploadNewFile';
const AVATAR_BASE64_INPUT_ID = 'avatarBase64';
const AVATAR_CROP_NAMESPACE = '.naisAvatarCrop';
const AVATAR_REMOVE_NAMESPACE = '.naisAvatarRemove';

function getStringData(element: HTMLElement, name: string, fallback = ''): string {
    const value = element.dataset[name];

    return value !== undefined && value !== '' ? value : fallback;
}

function ensureAvatarBase64Input(form: HTMLFormElement): HTMLInputElement {
    const existing = form.querySelector<HTMLInputElement>(`#${AVATAR_BASE64_INPUT_ID}`);

    if (existing) {
        return existing;
    }

    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.id = AVATAR_BASE64_INPUT_ID;
    hidden.name = AVATAR_BASE64_INPUT_ID;
    form.appendChild(hidden);

    return hidden;
}

function clearAvatarBase64Input(form: HTMLFormElement): void {
    form.querySelector<HTMLInputElement>(`#${AVATAR_BASE64_INPUT_ID}`)?.remove();
}

function setRemoveAvatar(form: HTMLFormElement, checked: boolean): void {
    const removeAvatar = form.querySelector<HTMLInputElement>('input[name="removeAvatar"]');

    if (!removeAvatar) {
        return;
    }

    removeAvatar.value = checked ? '1' : '0';
    removeAvatar.dispatchEvent(new Event('change', { bubbles: true }));
}

function updateFileInputPreview($input: JQuery<HTMLInputElement>, imageData: string): void {
    const $image = $input.closest('.file-input')
        .find('.file-preview-frame:visible img.file-preview-image, .file-preview-frame:visible img.kv-preview-data')
        .first();

    if ($image.length) {
        $image.attr('src', imageData);
    }
}

function openAvatarCropper(input: HTMLInputElement, $input: JQuery<HTMLInputElement>, imageData: string): void {
    if (!imageData) {
        return;
    }

    const form = input.form;

    if (!form) {
        return;
    }

    openCropper(imageData, croppedImage => {
        ensureAvatarBase64Input(form).value = croppedImage;
        setRemoveAvatar(form, false);
        updateFileInputPreview($input, croppedImage);
    }, 'avatar', input.id || 'avatar', {
        aspectRatio: 1,
        outputHeight: 450,
        outputMimeType: 'image/jpeg',
        outputQuality: 0.92,
        outputWidth: 450,
    });
}

function getPreviewImageUrl($frame: JQuery): string {
    return String(
        $frame.find('img.file-preview-image, img.kv-preview-data, img').first().attr('src') || ''
    );
}

function updateAvatarCropButtons($input: JQuery<HTMLInputElement>): void {
    $input.closest('.file-input').find('.file-preview-frame').each(function() {
        const $frame = $(this);
        if ($frame.hasClass('kv-zoom-thumb')) {
            return;
        }

        $frame.find('.kv-file-crop').toggle(getPreviewImageUrl($frame) !== '');
    });
}

function refreshAvatarLucideIcons($input: JQuery<HTMLInputElement>): void {
    const fileInputRoot = $input.closest('.file-input').get(0);
    if (fileInputRoot?.querySelector('i[data-lucide]')) {
        initLucideIcons(fileInputRoot);
    }

    const zoomModal = document.getElementById('kvFileinputModal');
    if (zoomModal?.querySelector('i[data-lucide]')) {
        initLucideIcons(zoomModal);
    }
}

function hasSelectedNewAvatar($input: JQuery<HTMLInputElement>): boolean {
    return $input.data(AVATAR_NEW_FILE_DATA) === true;
}

function removeInitialAvatarPreview($input: JQuery<HTMLInputElement>, $frame: JQuery): void {
    $frame.remove();
    $input.val('');

    const $fileInput = $input.closest('.file-input');
    if (!$fileInput.find('.file-preview-frame:not(.kv-zoom-thumb)').length) {
        $fileInput.addClass('file-input-new');
    }
}

function wrapAvatarInput($input: JQuery<HTMLInputElement>): void {
    const $field = $input.closest('[data-nais-field]');

    $field.addClass('nais-fileinput-upload-field');
    $field.children('label').addClass('form-label').removeClass('control-label');

    if ($input.parent('.nais-fileinput-upload').length) {
        return;
    }

    const $wrapper = $('<div class="nais-fileinput-upload" data-nais-fileinput-mode="single" data-nais-fileinput-style="compact"></div>');

    $input.before($wrapper);
    $wrapper.append($input);
}

function bindAvatarPreviewActions(
    input: HTMLInputElement,
    $input: JQuery<HTMLInputElement>,
    currentAvatarUrl: string
): void {
    const $fileInput = $input.closest('.file-input');

    $fileInput.off(`click${AVATAR_CROP_NAMESPACE}`, '.kv-file-crop');
    $fileInput.on(`click${AVATAR_CROP_NAMESPACE}`, '.kv-file-crop', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const imageUrl = getPreviewImageUrl($(this).closest('.file-preview-frame'));

        openAvatarCropper(input, $input, imageUrl);
    });

    $fileInput.off(`click${AVATAR_REMOVE_NAMESPACE}`, '.kv-file-remove');
    $fileInput.on(`click${AVATAR_REMOVE_NAMESPACE}`, '.kv-file-remove', function(e) {
        if (!input.form) {
            return;
        }

        const $frame = $(this).closest('.file-preview-frame');
        const removesCurrentAvatar = $frame.hasClass('file-preview-initial')
            && !hasSelectedNewAvatar($input)
            && currentAvatarUrl !== '';

        if (removesCurrentAvatar) {
            clearAvatarBase64Input(input.form);
            setRemoveAvatar(input.form, true);
            $input.data(AVATAR_NEW_FILE_DATA, false);
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            removeInitialAvatarPreview($input, $frame);
            return;
        }

        clearAvatarBase64Input(input.form);
        setRemoveAvatar(input.form, false);
    });
}

function initAvatarUpload(input: HTMLInputElement): void {
    const $input = $(input);

    if ($input.data(AVATAR_INITIALIZED_DATA)) {
        return;
    }

    if (typeof $input.fileinput !== 'function') {
        return;
    }

    const currentAvatarUrl = getStringData(input, 'currentAvatarUrl');
    const currentAvatarLabel = getStringData(input, 'currentAvatarLabel', 'Aktuální avatar');
    const locale = document.documentElement.lang || 'cs';
    const maxFileSize = Number(input.dataset.maxFileSize || 2048);

    wrapAvatarInput($input);

    const iconOptions = getFileInputIconOptions();

    $input.fileinput({
        ...iconOptions,
        allowedFileExtensions: ['jpg', 'jpeg', 'png', 'webp'],
        allowedFileTypes: ['image'],
        browseClass: FILE_INPUT_BROWSE_BUTTON_CLASS,
        dropZoneEnabled: false,
        fileActionSettings: {
            ...iconOptions.fileActionSettings,
            showDrag: false,
            showDownload: false,
            showRemove: true,
            showRotate: false,
            showUpload: false,
            showZoom: true,
            downloadClass: FILE_INPUT_HIDDEN_ACTION_CLASS,
            removeClass: FILE_INPUT_PREVIEW_ACTION_BUTTON_CLASS,
            removeErrorClass: FILE_INPUT_PREVIEW_ERROR_ACTION_BUTTON_CLASS,
            rotateClass: FILE_INPUT_HIDDEN_ACTION_CLASS,
            uploadClass: FILE_INPUT_HIDDEN_ACTION_CLASS,
            zoomClass: FILE_INPUT_PREVIEW_ACTION_BUTTON_CLASS,
        },
        initialPreview: currentAvatarUrl ? [currentAvatarUrl] : [],
        initialPreviewAsData: true,
        initialPreviewConfig: currentAvatarUrl
            ? [{ caption: currentAvatarLabel, key: 'avatar', showRemove: true, type: 'image' }]
            : [],
        language: locale.startsWith('cs') ? 'cs' : undefined,
        maxFileSize,
        overwriteInitial: true,
        previewFileType: 'image',
        showCancel: false,
        showCaption: false,
        showClose: false,
        showRemove: false,
        showUpload: false,
        layoutTemplates: getFileInputLayoutTemplates({
            crop: true,
            drag: false,
            restore: false,
        }),
        previewZoomButtonClasses: {
            borderless: FILE_INPUT_HIDDEN_ACTION_CLASS,
            fullscreen: FILE_INPUT_HIDDEN_ACTION_CLASS,
            toggleheader: FILE_INPUT_HIDDEN_ACTION_CLASS,
        },
    });

    bindAvatarPreviewActions(input, $input, currentAvatarUrl);
    $input.on('filezoomshown', () => refreshAvatarLucideIcons($input));

    $input.on('fileloaded filebatchselected', () => {
        if (input.form) {
            clearAvatarBase64Input(input.form);
            setRemoveAvatar(input.form, false);
        }
        $input.data(AVATAR_NEW_FILE_DATA, true);
        updateAvatarCropButtons($input);
        refreshAvatarLucideIcons($input);
    });
    $input.on('filecleared fileremoved', () => {
        if (input.form) {
            const removesCurrentAvatar = !hasSelectedNewAvatar($input) && currentAvatarUrl !== '';

            clearAvatarBase64Input(input.form);
            setRemoveAvatar(input.form, removesCurrentAvatar);
        }
        $input.data(AVATAR_NEW_FILE_DATA, false);
        updateAvatarCropButtons($input);
        refreshAvatarLucideIcons($input);
    });
    $input.on('fileimageloaded fileimagesloaded', () => {
        updateAvatarCropButtons($input);
        refreshAvatarLucideIcons($input);
    });

    updateAvatarCropButtons($input);
    refreshAvatarLucideIcons($input);
    $input.data(AVATAR_INITIALIZED_DATA, true);
}

export function initUsersAddEdit(root: HTMLElement): void {
    root.querySelectorAll<HTMLInputElement>(AVATAR_UPLOAD_SELECTOR).forEach(initAvatarUpload);
}
